import React, { useEffect, useMemo, useState } from 'react'
import './DashboardCust.css'
import { Link } from 'react-router-dom'
import { IoMdHourglass } from 'react-icons/io'
import { BiCalendar, BiCheckCircle, BiMoney } from 'react-icons/bi'
import { BsSearch } from 'react-icons/bs'
import Axios from 'axios'
import { IoStar } from 'react-icons/io5'
import { API_URL } from '../../config'

const url = `${API_URL}/apps/kscanner/webservices.php`

const FILTERS = [
  ['all', 'All'],
  ['upcoming', 'Upcoming'],
  ['ongoing', 'Ongoing'],
  ['past', 'Past'],
  ['draft', 'Draft'],
]

const statusOf = (event) =>
  event.budget <= 0 ? 'draft' : String(event.eventstatus || '').toLowerCase()

const organiserOf = (event) => event.organiser || event.businessname || '—'

const StatCard = ({ title, count, icon, tone, hot }) => (
  <article className={`kx-dc-stat kx-dc-stat--${tone} ${hot ? 'kx-dc-stat--hot' : ''}`}>
    <span className="kx-dc-stat__icon" aria-hidden="true">
      {icon}
    </span>
    <div>
      <small>{title}</small>
      <strong>{count}</strong>
    </div>
  </article>
)

const EventRow = ({ event }) => {
  const status = statusOf(event)
  const organiser = organiserOf(event)

  return (
    <div className="kx-dc-table__row" role="row">
      <div data-label="Organiser" className="kx-dc-organiser" title={organiser}>
        {organiser}
      </div>

      <div data-label="Event" className="kx-dc-event">
        <span className="kx-dc-event__poster" aria-hidden="true">
          {event.eventname ? event.eventname.charAt(0).toUpperCase() : ''}
        </span>
        <span className="kx-dc-event__name" title={event.eventname}>
          {event.eventname}
        </span>
        {event.is_sponsorship == 1 && (
          <IoStar className="kx-dc-event__star" aria-label="Sponsored" />
        )}
      </div>

      <div data-label="Date">{event.eventday}</div>

      <div data-label="Status">
        <span className={`kx-dc-badge kx-dc-badge--${status}`}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
        </span>
      </div>

      <div data-label="Purchased">{event.sold}</div>
      <div data-label="Admission">{event.admissions}</div>
      <div data-label="Transaction" className="kx-dc-money">
        R{event.sales ?? 0}
      </div>

      <div className="kx-dc-actions">
        <Link
          to={{
            pathname: `./transactions-view`,
            search: `q_id=${event.eventid}`,
          }}
          className="kx-dc-view"
        >
          View <span aria-hidden="true">→</span>
        </Link>
      </div>
    </div>
  )
}

const EventsDashboardCust = () => {
  const [ongoingEvents, setOngoingEvents] = useState(0)
  const [upcomingEvents, setUpcomingEvents] = useState(0)
  const [pastEvents, setPastEvents] = useState(0)
  const [eventList, setEventList] = useState([])
  const [revenue, setRevenue] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')

  const customername = localStorage.getItem('customername')
  const firstName = customername ? customername.trim().split(' ')[0] : ''

  const fetchongoingEventsData = async () => {
    Axios.get(`${url}?query=FETCH-EVENT-COUNTS`, { withCredentials: true })
      .then((response) => {
        setOngoingEvents(response.data.ongoing)
        setUpcomingEvents(response.data.upcoming)
        setPastEvents(response.data.past)
        setRevenue(response.data.revenue)
      })
      .catch((error) => {
        console.error('Error fetching dashboard data:', error)
      })
  }

  const fetchEvents = async () => {
    Axios.get(`${url}?query=EVENT-LIST`, { withCredentials: true })
      .then((response) => {
        if (response.data.status === 'success') {
          setEventList(response.data.events)
        } else {
          setEventList([])
        }
        setLoading(false)
      })
      .catch((error) => {
        setEventList([])
        setLoading(false)
        console.error('Error fetching dashboard data:', error)
      })
  }

  useEffect(() => {
    fetchongoingEventsData()
    fetchEvents()
  }, [])

  const counts = useMemo(() => {
    const c = { all: eventList.length, upcoming: 0, ongoing: 0, past: 0, draft: 0 }
    eventList.forEach((event) => {
      const s = statusOf(event)
      if (c[s] !== undefined) c[s] += 1
    })
    return c
  }, [eventList])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return eventList.filter((event) => {
      if (filter !== 'all' && statusOf(event) !== filter) return false
      if (!q) return true
      return (
        String(event.eventname || '')
          .toLowerCase()
          .includes(q) || organiserOf(event).toLowerCase().includes(q)
      )
    })
  }, [eventList, filter, query])

  const tabs = FILTERS.filter(([id]) => id !== 'draft' || counts.draft > 0)

  return (
    <div className="kx-dc">
      {/* HEADER */}
      <div className="kx-dc-head">
        <div>
          <span className="kx-dc-pill">
            <i /> Customer dashboard
          </span>
          <h1>Dashboard</h1>
          <p>
            {firstName ? `Welcome back, ${firstName}. ` : ''}Your events, tickets and spending in
            one place.
          </p>
        </div>

        <label className="kx-dc-search">
          <BsSearch size={15} aria-hidden="true" />
          <input
            type="text"
            placeholder="Search events or organisers"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
              ×
            </button>
          )}
        </label>
      </div>

      {/* STATS */}
      <div className="kx-dc-stats">
        <StatCard
          title="Upcoming Events"
          count={upcomingEvents}
          icon={<BiCalendar />}
          tone="mint"
        />
        <StatCard
          title="Ongoing Events"
          count={ongoingEvents}
          icon={<IoMdHourglass />}
          tone="sun"
        />
        <StatCard title="Past Events" count={pastEvents} icon={<BiCheckCircle />} tone="peach" />
        <StatCard title="Outflow" count={`R${revenue}`} icon={<BiMoney />} tone="brand" hot />
      </div>

      {/* EVENTS */}
      <section className="kx-dc-panel">
        <div className="kx-dc-panel__bar">
          <div className="kx-dc-tabs" role="tablist" aria-label="Filter events">
            {tabs.map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={filter === id}
                className={filter === id ? 'is-active' : ''}
                onClick={() => setFilter(id)}
              >
                {label}
                <b>{counts[id]}</b>
              </button>
            ))}
          </div>
          <span className="kx-dc-panel__count">
            {visible.length} {visible.length === 1 ? 'event' : 'events'}
          </span>
        </div>

        <div className="kx-dc-table" role="table">
          <div className="kx-dc-table__head" role="row">
            <span>Organiser</span>
            <span>Event</span>
            <span>Date</span>
            <span>Status</span>
            <span>Purchased</span>
            <span>Admission</span>
            <span>Transaction</span>
            <span />
          </div>

          {loading &&
            [0, 1, 2, 3].map((i) => (
              <div className="kx-dc-table__row kx-dc-skeleton" role="row" key={i}>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
            ))}

          {!loading && eventList.length === 0 && (
            <div className="kx-dc-empty">
              <strong>No events found.</strong>
              <p>Tickets you buy will show up here.</p>
              <Link
                to={{
                  pathname: './',
                  state: { prevpage: 'dashboard' },
                }}
                className="kx-dc-view"
              >
                Buy your first ticket <span aria-hidden="true">→</span>
              </Link>
            </div>
          )}

          {!loading && eventList.length > 0 && visible.length === 0 && (
            <div className="kx-dc-empty">
              <strong>No matching events</strong>
              <p>Try another filter or search.</p>
            </div>
          )}

          {!loading &&
            visible.map((event, index) => <EventRow key={event.eventid ?? index} event={event} />)}
        </div>
      </section>
    </div>
  )
}

export default EventsDashboardCust
